import { useTranslation } from 'react-i18next';
import type { User } from '../../types';
import type { AdminProjectListItem } from '../../api/adminProjects';
import UserSearchInput from '../../components/UserSearchInput';

interface TransferOwnerModalProps {
  target: AdminProjectListItem;
  newOwnerId: string;
  transferring: boolean;
  onSelectNewOwner: (user: User | null) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

// TransferOwnerModal isolates the admin transfer-owner dialog. It searches users
// by username on demand (UserSearchInput) instead of holding every user in
// memory, so the dialog scales when there are many accounts. The parent owns the
// resolved newOwnerId because onConfirm relies on it; the modal forwards the
// picked user via onSelectNewOwner (null when the field is cleared/edited).
export default function TransferOwnerModal({
  target,
  newOwnerId,
  transferring,
  onSelectNewOwner,
  onCancel,
  onConfirm,
}: TransferOwnerModalProps) {
  const { t } = useTranslation('admin');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('transferOwner.dialogTitle')}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        className="card"
        style={{
          background: 'var(--surface)',
          padding: '1.2rem 1.5rem',
          minWidth: 360,
          maxWidth: 480,
          boxShadow: '0 6px 24px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem' }}>{t('transferOwner.dialogTitle')}</h2>
        <div style={{ fontSize: '0.88rem', color: 'var(--text-2)', marginBottom: '0.75rem' }}>
          {t('transferOwner.project')}<strong>{target.name}</strong>
        </div>
        <div style={{ fontSize: '0.88rem', color: 'var(--text-2)', marginBottom: '1rem' }}>
          {t('transferOwner.currentOwner')}{target.ownerName || target.ownerId || '-'}
        </div>
        <label
          style={{
            display: 'block',
            fontSize: '0.85rem',
            color: 'var(--text-2)',
            marginBottom: '0.35rem',
          }}
        >
          {t('transferOwner.newOwner')}
        </label>
        <UserSearchInput
          ariaLabel={t('transferOwner.newOwnerAriaLabel')}
          placeholder={t('transferOwner.newOwnerPlaceholder')}
          excludeUserId={target.ownerId}
          onSelect={onSelectNewOwner}
          style={{ marginBottom: '0.6rem' }}
        />
        <div style={{ minHeight: '1.2rem', marginBottom: '0.6rem', fontSize: '0.78rem' }}>
          {!newOwnerId && (
            <span style={{ color: 'var(--text-3)' }}>
              {t('transferOwner.pickHint')}
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: '0.78rem',
            color: 'var(--text-3)',
            marginBottom: '1rem',
            lineHeight: 1.5,
          }}
        >
          {t('transferOwner.transferNote')}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={transferring}>
            {t('cancel', { ns: 'common' })}
          </button>
          <button
            className="btn btn-primary"
            onClick={onConfirm}
            disabled={!newOwnerId || transferring}
          >
            {transferring ? t('transferOwner.transferring') : t('transferOwner.confirmTransfer')}
          </button>
        </div>
      </div>
    </div>
  );
}
